import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiClient } from '@/lib/api-client';
import { Flag, CheckCircle, XCircle, AlertTriangle, Clock, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store';
import { Navigate } from 'react-router-dom';

type ReportStatus = 'pending' | 'reviewing' | 'resolved' | 'dismissed';
type ActionTaken = 'removed' | 'warned' | 'banned' | 'no_action';

interface Report {
  id: string;
  reporter_username: string;
  reported_username: string;
  reported_entity_type: string;
  reported_entity_id: string;
  reason: string;
  description?: string;
  status: ReportStatus;
  action_taken?: ActionTaken;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending:   'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  reviewing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  resolved:  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  dismissed: 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400',
};

const REASON_LABELS: Record<string, string> = {
  spam:          'Spam',
  scam:          'Scam / Fraud',
  harassment:    'Harassment',
  inappropriate: 'Inappropriate',
  copyright:     'Copyright',
  other:         'Other',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminModerationPage() {
  const user = useAuthStore((state) => state.user);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [reviewing, setReviewing] = useState<Report | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const queryClient = useQueryClient();

  // Only admins can see this page
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const { data, isLoading } = useQuery<Report[]>(
    ['admin-reports', statusFilter],
    async () => {
      const res = await apiClient.get<any>(`/admin/reports?status=${statusFilter}`);
      return res.data?.data ?? [];
    },
    { refetchInterval: 30000 }
  );

  const { mutate: reviewReport, isLoading: submitting } = useMutation(
    ({ id, status, action_taken, resolution_notes }: {
      id: string; status: string; action_taken: ActionTaken; resolution_notes?: string;
    }) =>
      apiClient.patch(`/admin/reports/${id}`, { status, action_taken, resolution_notes }),
    {
      onSuccess: () => {
        toast.success('Report reviewed');
        queryClient.invalidateQueries(['admin-reports']);
        setReviewing(null);
        setResolutionNotes('');
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error?.message || 'Failed to update report');
      },
    }
  );

  const handleAction = (report: Report, status: 'resolved' | 'dismissed', action: ActionTaken) => {
    reviewReport({ id: report.id, status, action_taken: action, resolution_notes: resolutionNotes || undefined });
  };

  const reports = data ?? [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950">
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-10">
        <div className="container-custom">
          <h1 className="text-3xl font-display font-bold flex items-center gap-3">
            <Flag className="h-7 w-7" />
            Content Moderation
          </h1>
          <p className="text-red-200 mt-1">Review reported content and take action</p>
        </div>
      </div>

      <div className="container-custom py-8">
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <Filter className="h-4 w-4 text-gray-400 dark:text-zinc-500" />
          {(['pending', 'reviewing', 'resolved', 'dismissed'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-brand-600 text-white'
                  : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Reports list */}
        {isLoading ? (
          <div className="text-center py-12 text-gray-400 dark:text-zinc-600">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 p-12 text-center">
            <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-zinc-500">No {statusFilter} reports</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => (
              <div
                key={report.id}
                className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`badge ${STATUS_COLORS[report.status]}`}>
                        {report.status}
                      </span>
                      <span className="badge bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300">
                        {REASON_LABELS[report.reason] ?? report.reason}
                      </span>
                      <span className="badge bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 capitalize">
                        {report.reported_entity_type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-zinc-500">
                      Reported by <strong className="text-gray-700 dark:text-zinc-300">@{report.reporter_username}</strong>
                      {report.reported_username && (
                        <> against <strong className="text-gray-700 dark:text-zinc-300">@{report.reported_username}</strong></>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-zinc-600 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(report.created_at)}
                    </p>
                  </div>
                  {report.action_taken && (
                    <span className="text-xs text-gray-500 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded-lg">
                      Action: {report.action_taken.replace('_', ' ')}
                    </span>
                  )}
                </div>

                {report.description && (
                  <p className="text-sm text-gray-700 dark:text-zinc-300 bg-gray-50 dark:bg-zinc-800 rounded-lg p-3 mb-4">
                    "{report.description}"
                  </p>
                )}

                {report.status === 'pending' && (
                  <>
                    {reviewing?.id === report.id && (
                      <div className="mb-3">
                        <textarea
                          value={resolutionNotes}
                          onChange={(e) => setResolutionNotes(e.target.value)}
                          placeholder="Resolution notes (optional)..."
                          rows={2}
                          className="input resize-none text-sm"
                        />
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setReviewing(report);
                          handleAction(report, 'resolved', 'removed');
                        }}
                        disabled={submitting}
                        className="btn-danger text-sm py-1.5 px-3"
                      >
                        Remove Content
                      </button>
                      <button
                        onClick={() => {
                          setReviewing(report);
                          handleAction(report, 'resolved', 'warned');
                        }}
                        disabled={submitting}
                        className="btn-secondary text-sm py-1.5 px-3"
                      >
                        Warn User
                      </button>
                      <button
                        onClick={() => {
                          setReviewing(report);
                          handleAction(report, 'resolved', 'banned');
                        }}
                        disabled={submitting}
                        className="bg-orange-600 hover:bg-orange-700 text-white btn text-sm py-1.5 px-3"
                      >
                        Ban User
                      </button>
                      <button
                        onClick={() => {
                          setReviewing(report);
                          handleAction(report, 'dismissed', 'no_action');
                        }}
                        disabled={submitting}
                        className="btn-ghost text-sm py-1.5 px-3"
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" />
                        Dismiss
                      </button>
                      {reviewing?.id !== report.id && (
                        <button
                          onClick={() => setReviewing(reviewing?.id === report.id ? null : report)}
                          className="btn-ghost text-sm py-1.5 px-3"
                        >
                          Add Notes
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
